import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useMemo } from 'react'

import { ApiPagedPayload } from '@/api/shared/types'
import { useSelectedProjectStore } from '@/components/entities/common/model/selected-project.store'
import { PageType } from '@/components/entities/requests/model/types'
import {
  FilterFormData,
  SelectedQuickFilters
} from '@/components/widgets/request/FiltersPopover/types'

import { buildRequestsList } from '../lib/requests.mapper'
import { RequestsService } from '../lib/requests.service'
import type { RequestTypeType } from '../lib/requests.types'
import { useRequestsStatuses } from './useGetRequestsStatuses'

export const REQUESTS_LIST_RQ_KEY = 'requests-list'

type Props = {
  isPageType: PageType
  searchQuery?: string
  filters?: FilterFormData
  quickFilters?: SelectedQuickFilters
  requestTypes?: RequestTypeType[]
}

export const useRequestsList = ({
  isPageType,
  searchQuery,
  filters,
  quickFilters,
  requestTypes,
  page,
  pageSize
}: ApiPagedPayload<Props>) => {
  const { selectedProjectId: projectId } = useSelectedProjectStore()
  const {
    status,
    data,
    error: requestsError,
    isLoading
  } = useQuery({
    queryKey: [
      REQUESTS_LIST_RQ_KEY,
      projectId,
      isPageType,
      searchQuery,
      filters?.period,
      filters?.premises,
      JSON.stringify(quickFilters),
      filters?.statuses,
      filters?.payable,
      filters?.passTypes,
      page
    ],
    queryFn: () => {
      const payload = {
        projectId,
        ...quickFilters,
        ...filters?.statuses,
        ...filters?.payable,
        query: searchQuery,
        dateFrom: filters?.period?.from
          ? dayjs(filters?.period.from).format('YYYY-MM-DD')
          : undefined,
        dateTo: filters?.period?.to
          ? dayjs(filters?.period.to).format('YYYY-MM-DD')
          : undefined,
        premises: filters?.premises,
        pageSize,
        page
      }

      return RequestsService.getRequests(payload)
    },
    staleTime: 0
  })

  const {
    data: requestStatuses,
    error: requestStatusesError,
    isLoading: areStatusesLoading
  } = useRequestsStatuses(true)

  const mappedData = useMemo(
    () => ({
      ...data,
      data: buildRequestsList(data?.data, requestTypes, requestStatuses)
    }),
    [data, requestStatuses, requestTypes]
  )

  return {
    data: mappedData,
    error: requestsError ?? requestStatusesError,
    areStatusesLoading,
    isLoading,
    status
  }
}
